import React from "react";
import { motion } from "framer-motion";
import { Globe, Link2, ShoppingBag, Truck } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";

const ICONS = [Globe, Link2, ShoppingBag, Truck];

export default function HowItWorks() {
  const { t } = useLanguage();

  return (
    <section
      id="how"
      data-testid="how-it-works-section"
      className="relative overflow-hidden bg-[#FAFAFA] py-24 text-[#111] md:py-32 dark:bg-[#080808] dark:text-white"
    >
      {/* ambient orange glow */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[700px] h-[400px] bg-[#F37021]/10 blur-[120px] rounded-full pointer-events-none" />

      <div className="relative mx-auto max-w-7xl px-5 md:px-8">
        <div className="mb-14 flex flex-col gap-6 lg:mb-20 lg:flex-row lg:items-end lg:justify-between">
          <div className="max-w-3xl">
            <span className="mb-4 inline-block text-xs font-semibold uppercase tracking-[0.3em] text-[#F37021]">
              — {t.how.eyebrow}
            </span>
            <h2 className="max-w-2xl font-display text-4xl font-bold leading-[1.08] sm:text-5xl md:text-6xl">
              {t.how.title}
            </h2>
          </div>
          <p className="max-w-xl text-base leading-relaxed text-[#5B6473] md:text-lg lg:max-w-md dark:text-[#C1CBD9]">
            {t.how.subtitle}
          </p>
        </div>

        {/* Steps grid */}
        <div className="relative grid grid-cols-1 gap-5 md:grid-cols-2 md:gap-6 lg:grid-cols-4">
          {/* Connecting dashed line (lg only) */}
          <div className="hidden lg:block absolute top-[68px] left-[12%] right-[12%] h-px">
            <svg width="100%" height="2" preserveAspectRatio="none">
              <line x1="0" y1="1" x2="100%" y2="1" stroke="#F37021" strokeWidth="1.5" strokeDasharray="6 8" opacity="0.35" />
            </svg>
          </div>

          {t.how.steps.map((step, i) => {
            const Icon = ICONS[i];
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className="group relative rounded-[28px] border border-black/6 bg-white p-7 transition-all hover:-translate-y-1 hover:border-[#F37021]/28 hover:bg-[#FFF7F1] hover:shadow-[0_26px_60px_rgba(16,24,40,0.12)] dark:border-white/10 dark:bg-[linear-gradient(180deg,#101010_0%,#181818_100%)] dark:hover:bg-[#1D1D1D] dark:hover:shadow-[0_26px_60px_rgba(0,0,0,0.28)]"
                data-testid={`how-step-${i}`}
              >
                {/* Number + icon */}
                <div className="mb-5 flex items-center justify-between">
                  <span className="font-display text-5xl font-bold leading-none text-[#F37021]">
                    {step.n}
                  </span>
                  <div className="flex h-11 w-11 items-center justify-center rounded-2xl border border-[#F37021]/18 bg-[#F37021]/10 transition-colors group-hover:border-[#F37021] group-hover:bg-[#F37021]">
                    <Icon className="h-5 w-5 text-[#F37021] transition-colors group-hover:text-white" strokeWidth={1.8} />
                  </div>
                </div>

                <h3 className="mb-2.5 font-display text-xl font-semibold">
                  {step.title}
                </h3>
                <p className="text-sm leading-relaxed text-[#5B6473] dark:text-[#C8D3E1]">
                  {step.desc}
                </p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
